import {useState, useMemo, useCallback} from "react";
import {useLocalSearchParams, router} from "expo-router";
import {OrganizationDashboard} from "@/components/organization/OrganizationDashboard";
import {OrganizationSwitcherSheet} from "@/components/organization/OrganizationSwitcherSheet";
import {useOrganizationNavigation} from "@/hooks/useOrganizationNavigation";
import {useAppData, useOrganizationData} from "@/data/app-data-hooks";
import {useSetLastSelectedOrg} from "@/data/user-data-hooks";

export default function OrganizationDetailIndex() {
    const {orgId} = useLocalSearchParams<{ orgId: string }>();
    const navigation = useOrganizationNavigation(orgId);
    const {data} = useAppData();
    const {data: orgData} = useOrganizationData(orgId);
    const setLastSelectedOrg = useSetLastSelectedOrg();
    const canInvite = orgData?.role === 'owner' || orgData?.role === 'leader';
    const [switcherVisible, setSwitcherVisible] = useState(false);

    const orgIds = useMemo(() => Object.keys(data), [data]);
    const hasMultipleOrgs = orgIds.length > 1;

    const organizations = useMemo(() =>
        orgIds.map(id => ({id, organization: data[id].organization})),
        [data, orgIds]
    );

    const handleSwitchOrg = useCallback(() => {
        setSwitcherVisible(true);
    }, []);

    const handleSelectOrg = useCallback((selectedOrgId: string) => {
        setSwitcherVisible(false);
        if (selectedOrgId !== orgId) {
            setLastSelectedOrg.mutate(selectedOrgId);
            router.replace(`/organization/${selectedOrgId}`);
        }
    }, [orgId, setLastSelectedOrg]);

    return (
        <>
            <OrganizationDashboard
                orgId={orgId}
                onNavigateToAchievements={navigation.navigateToAchievements}
                onNavigateToAllVideos={navigation.navigateToLibrary}
                onNavigateToMedia={navigation.navigateToLibrary}
                onNavigateToProfile={navigation.navigateToProfile}
                onNavigateToMembership={navigation.navigateToMembershipPlan}
                onNavigateToOffers={navigation.navigateToOffers}
                onNavigateToChat={data[orgId]?.organization?.chatEnabled ? navigation.navigateToChat : undefined}
                onNavigateToInvite={canInvite ? navigation.navigateToInvite : undefined}
                onPlayVideo={navigation.navigateToVideo}
                onNavigateToBookmarkedVideos={navigation.navigateToBookmarks}
                onNavigateToLastViewedVideos={navigation.navigateToLastViewedVideos}
                onSwitchOrg={hasMultipleOrgs ? handleSwitchOrg : undefined}
                orgCount={orgIds.length}
            />
            {hasMultipleOrgs && (
                <OrganizationSwitcherSheet
                    visible={switcherVisible}
                    organizations={organizations}
                    currentOrgId={orgId}
                    onSelectOrg={handleSelectOrg}
                    onClose={() => setSwitcherVisible(false)}
                />
            )}
        </>
    );
}
